<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import ModalDialog from '@components/ModalDialog.vue'
    import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

    const props = defineProps({
        title: {
            type: String,
            required: true
        },
        forgotUrl: {
            type: String,
            required: true
        },
        registerUrl: {
            type: String,
            required: true
        }
    })

    const isOpen = ref(false);

    const email = ref('');

    const password = ref('');

    const remember = ref(false);

    const error = ref('');

    const loading = ref(false);

    const emailInput = ref(null);

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    // Set when the modal was opened from the header navbar, so login lands on the user's profile.
    const toProfile = ref(false);

    const open = (goToProfile = false) => {
        error.value = '';
        toProfile.value = goToProfile === true;
        isOpen.value = true;
    }

    // Every plain link to /login opens the modal instead (Blade and Vue markup alike), so
    // the links still work as normal links without JS or when opened in a new tab.
    const onClick = (e) => {
        const link = e.target.closest('a[href="/login"]');
        if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }
        e.preventDefault();
        open(link.hasAttribute('data-login-to-profile'));
    }

    const submit = () => {
        if (loading.value) {
            return;
        }
        loading.value = true;
        error.value = '';

        const data = { email: email.value, password: password.value };
        // The controller only checks whether "remember" is present, so leave it out when unticked.
        if (remember.value) {
            data.remember = 'on';
        }
        if (toProfile.value) {
            data.to_profile = 1;
        }

        axios.post('/login', data, {
            headers: { 'X-CSRF-TOKEN': csrfToken }
        }).then((response) => {
            if (response.data?.redirect) {
                window.location.href = response.data.redirect;
            } else {
                window.location.reload();
            }
        }).catch((err) => {
            loading.value = false;
            const errors = err.response?.data?.errors;
            if (err.response?.status === 422 && errors) {
                error.value = Object.values(errors)[0][0];
            } else {
                // Expired session (419) or anything unexpected: fall back to the full login page.
                window.location.href = '/login';
            }
        });
    }

    watch(isOpen, (value) => {
        if (value) {
            nextTick(() => emailInput.value?.focus());
        }
    }, { flush: 'post' });

    onMounted(() => {
        document.addEventListener('click', onClick);
        window.addEventListener('open-login', open);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('click', onClick);
        window.removeEventListener('open-login', open);
    });
</script>

<template>
    <ModalDialog v-model:open="isOpen" :title="title">
        <template #title>
            <span class="inline-flex items-center gap-2.5 text-[1.35rem] font-semibold text-white">
                <span class="inline-block w-2 h-2 rounded-full bg-crimson shadow-[0_0_12px_var(--color-crimson)] animate-pulse" aria-hidden="true"></span>
                {{ title }}
            </span>
        </template>
        <div class="absolute inset-0 -z-10 overflow-hidden rounded-[18px] pointer-events-none" aria-hidden="true">
            <div class="absolute -top-24 -left-16 w-72 h-72 rounded-full bg-crimson/25 blur-3xl"></div>
            <div class="absolute -bottom-28 -right-20 w-72 h-72 rounded-full bg-orchid/20 blur-3xl"></div>
            <div class="absolute inset-x-8 top-0 h-px bg-linear-to-r from-transparent via-crimson to-transparent"></div>
        </div>
        <form method="POST" action="/login" class="px-6 pb-6" @submit.prevent="submit">
            <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
            <div class="form-field">
                <label for="login-modal-email" class="form-label">{{ __('E-Mail Address') }}</label>
                <input id="login-modal-email" ref="emailInput" v-model="email" type="email" name="email" class="form-input" :class="{ 'is-invalid': error }" autocomplete="email" required>
            </div>
            <div class="form-field">
                <label for="login-modal-password" class="form-label">{{ __('Password') }}</label>
                <input id="login-modal-password" v-model="password" type="password" name="password" class="form-input" :class="{ 'is-invalid': error }" autocomplete="current-password" required>
            </div>
            <div class="flex flex-wrap items-center justify-between gap-2 mb-5">
                <div class="form-check mb-0!">
                    <input id="login-modal-remember" v-model="remember" type="checkbox" name="remember">
                    <label for="login-modal-remember">{{ __('Remember Me') }}</label>
                </div>
                <a :href="forgotUrl" class="text-[.9rem]">{{ __('Forgot Your Password?') }}</a>
            </div>
            <button type="submit" class="btn btn-primary btn-lg w-full" :disabled="loading">
                <span v-if="loading" class="inline-block w-4 h-4 mr-2 align-[-2px] rounded-full border-2 border-white/40 border-t-white animate-spin" aria-hidden="true"></span>
                {{ __('Login') }}
            </button>
            <div class="flex items-center justify-center gap-3 pt-5 mt-6 border-t border-line text-[.95rem] text-muted">
                {{ __('Not registered? Register here') }}
                <a :href="registerUrl" class="btn btn-secondary btn-sm">{{ __('Join') }}</a>
            </div>
        </form>
    </ModalDialog>
</template>
